import React, { FC, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import { ClipboardList, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import type { IConsultation } from '../../store/types';
import { getTime } from '../../utils/getTime';
import { navigateToJournalSession } from '../../pages/journal/journal-navigation';
import { Alert, Chip, EmptyState, Pressable } from '../../shared/ui';

import { JournalConsultationFormatIcon } from './journal-consultation-format-icon';

/** Подписи чипов и подписи вкладок в SessionDetail (должны совпадать с `reportTypes` там). */
const aiAssistants: { label: string; sessionReportLabel: string }[] = [
  { label: 'ИИ супервизия', sessionReportLabel: 'ИИ супервизия' },
  { label: 'Конспект', sessionReportLabel: 'Конспект' },
  { label: 'Домашнее задание', sessionReportLabel: 'Домашнее задание' },
  { label: 'Метафоры', sessionReportLabel: 'Метафоры' },
  { label: 'ИИ ICF коуч', sessionReportLabel: 'ИИ ICF коуч' },
  { label: 'Сказкотерапия', sessionReportLabel: 'Сказкотерапия' },
];

export interface AllSessionsProps {
  consultations: IConsultation[];
  onOpenSessionDetail?: (consultationId: number, initialReportLabel: string) => void;
}

const truncate = (text: string, maxLen: number) => {
  const trimmed = text.trim();
  if (trimmed.length <= maxLen) return trimmed;
  return `${trimmed.slice(0, maxLen)}...`;
};

const getSessionTitle = (consultation: IConsultation) => {
  const first = consultation.result?.[0];

  const fromConclusion = first?.result?.trim();
  if (fromConclusion) return truncate(fromConclusion.split('\n')[0] ?? fromConclusion, 70);

  const fromDpdg = first?.dpdg?.comment?.trim();
  if (fromDpdg) return truncate(fromDpdg.split('\n')[0] ?? fromDpdg, 70);

  const fromQuestion = first?.questions?.[0]?.question?.trim();
  if (fromQuestion) return truncate(fromQuestion.split('\n')[0] ?? fromQuestion, 70);

  return 'Без названия';
};

const getSessionSummary = (consultation: IConsultation) => {
  const first = consultation.result?.[0];
  const candidate = first?.dpdg?.comment?.trim() || first?.result?.trim();
  if (!candidate) return null;
  return truncate(candidate.split('\n')[0] ?? candidate, 160);
};

export const AllSessions: FC<AllSessionsProps> = ({ consultations, onOpenSessionDetail }) => {
  const navigate = useNavigate();

  const openSession = useCallback(
    (consultation: IConsultation, initialTab: string) => {
      if (onOpenSessionDetail) {
        onOpenSessionDetail(consultation.id, initialTab);
        return;
      }
      navigateToJournalSession(navigate, consultation.id, consultation.client_entity, initialTab);
    },
    [navigate, onOpenSessionDetail],
  );

  const sessions = useMemo(() => {
    return consultations.map((consultation, index) => {
      const hasReports = true;
      const title = getSessionTitle(consultation);
      const summary = getSessionSummary(consultation);
      const isUntitled = title === 'Без названия';

      const dateLabel = `${dayjs(new Date(consultation.date)).format('DD.MM.YYYY')} ${getTime(
        new Date(consultation.datetime_start),
      )}`;

      return {
        id: consultation.id,
        consultation,
        orderNumber: consultation.consultation_number ?? index + 1,
        dateLabel,
        title: isUntitled ? 'Сессия' : title,
        rawTitle: title,
        summary,
        hasReports,
      };
    });
  }, [consultations]);

  if (sessions.length === 0) {
    return <EmptyState>Для выбранного клиента ещё нет сессий</EmptyState>;
  }

  return (
    <div className="space-y-4">
      <Alert quiet icon={<span aria-hidden>⏰</span>} title="Прошло время с последней сессии — нужен быстрый повтор?">
        Освежите память перед следующей встречей
      </Alert>

      {sessions.map((session) => (
        <div
          key={session.id}
          className="bg-white border border-line rounded-control p-5 hover:shadow-raised transition-shadow"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 flex-1 items-start gap-4">
              <span className="w-5 shrink-0 text-body-l-strong leading-tight text-ink">{session.orderNumber}</span>
              <JournalConsultationFormatIcon consultation={session.consultation} />
              <div className="min-w-0 flex-1">
                <p className="text-ui-xs text-ink-subtle mb-1">{session.dateLabel}</p>

                <Pressable
                  className="text-ink font-medium hover:text-primary transition-colors text-left w-full truncate"
                  onClick={() => openSession(session.consultation, 'Инструменты')}
                  title={session.rawTitle}
                >
                  {session.title}
                </Pressable>

                {session.summary && <p className="text-ui text-ink-subtle mt-1">{session.summary}</p>}
              </div>
            </div>
          </div>

          {session.hasReports && (
            <div className="mt-4 flex flex-wrap gap-2">
              <Chip
                onClick={() => openSession(session.consultation, 'Инструменты')}
                icon={<ClipboardList size={14} strokeWidth={2} aria-hidden />}
              >
                Инструменты
              </Chip>

              <Chip
                onClick={() => openSession(session.consultation, 'Транскрипция')}
                icon={<ClipboardList size={14} strokeWidth={2} aria-hidden />}
              >
                Транскрипция
              </Chip>

              {aiAssistants.map((assistant) => (
                <Chip
                  key={assistant.sessionReportLabel}
                  onClick={() => openSession(session.consultation, assistant.sessionReportLabel)}
                  icon={<ClipboardList size={14} strokeWidth={2} aria-hidden />}
                >
                  {assistant.label}
                </Chip>
              ))}

              <Chip
                onClick={() => openSession(session.consultation, 'add')}
                icon={<Plus size={14} strokeWidth={2} aria-hidden />}
              >
                Добавить отчёт
              </Chip>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
